import InfoOutlined from "@mui/icons-material/InfoOutlined";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { DataWindow, useDataWindows, useMetaData } from "../../api/useMetaDataApi";
import { useHealthcareProviders, usePayerProfileTopHCPExport } from "../../api/usePayerProfileApi";
import BreadCrumbs from "../../components/BreadCrumbs/BreadCrumbs";
import PageHeader from "../../components/Headers/PageHeader";
import MinimalSelect from "../../components/Inputs/MinimalSelect";
import Select from "../../components/Inputs/Select";
import Table from "../../components/Table/Table";
import { Column } from "../../components/Table/types";
import Tooltip from "../../components/Tooltip/Tooltip";
import {
  useClientConfigurationLoaded,
  useClientIndicationFiltersconfiguration,
  useClientPlanProfileConfiguration,
  useIndicationBasedTopHCPConfiguration,
} from "../../data/global/useProductConfiguration";
import { usePayerLandscapeDispatch, usePayerLandscapeState } from "../../data/payerLandscape/hooks";
import { SortDirection } from "../../generate";
import useGetRole from "../../hooks/useGetRole";
import { reconcileIndicationFilter } from "../../utils/filterReconciliation";

const HealthcareProviderName = "healthcareProviderName" as const;
const useTableColumns = (channel: string, useIndicationInNames = false, indication?: string | null | undefined) => {
  const { client: selectedClient } = useParams<{ client: string }>();

  const {
    sharedFilters: { timePeriod },
  } = usePayerLandscapeState();
  const { t, i18n } = useTranslation();

  const client_indication_key = useMemo(() => {
    return useIndicationInNames ? selectedClient + "_" + indication?.replaceAll(" ", "") : selectedClient;
  }, [selectedClient, indication, useIndicationInNames]);

  const availableColumns: Column[] = useMemo(
    () => [
      {
        label: t([`planProfile.topHcps.${selectedClient}`, `planProfile.topHcps.default`]),
        align: "left",
        dataKey: "topHCPs",
        tdClasses: "bg-light-blue",
        component: HealthcareProviderName,
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.fav_pct`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.fav_pct`)
          : t("planProfile.topHcpColumnHeaders.fav_pct"),
        align: "right",
        dataKey: "fav_pct",
        format: {
          suffix: "%",
          decimalScale: 1,
          fixedDecimalScale: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.acc_pct`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.acc_pct`)
          : t("planProfile.topHcpColumnHeaders.acc_pct"),
        align: "right",
        dataKey: "acc_pct",
        format: {
          suffix: "%",
          decimalScale: 1,
          fixedDecimalScale: true,
        },
      },
      {
        label: t("planProfile.topHcpColumnHeaders.bobPctOvrl", { product: selectedClient }),
        align: "right",
        dataKey: "bob_pct_ovrl",
        format: {
          suffix: "%",
          decimalScale: 1,
          fixedDecimalScale: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.prod_trx`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.prod_trx`, { product: selectedClient })
          : t("planProfile.topHcpColumnHeaders.prod_trx", { product: selectedClient }),
        align: "right",
        dataKey: "prod_trx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.marketTrx`)
          ? t([
              `planProfile.topHcpColumnHeaders.${client_indication_key}.marketTrx.${timePeriod}`,
              `planProfile.topHcpColumnHeaders.${client_indication_key}.marketTrx`,
            ])
          : t(`planProfile.topHcpColumnHeaders.marketTrx`),
        align: "right",
        dataKey: "mkt_trx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: t("planProfile.topHcpColumnHeaders.prod_share", { product: selectedClient?.split("_")[0] }),
        align: "right",
        dataKey: "prod_share",
        format: {
          suffix: "%",
          decimalScale: 1,
          fixedDecimalScale: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.chnl_prod_trx`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.chnl_prod_trx`, {
              product: selectedClient,
              channel,
            })
          : t("planProfile.topHcpColumnHeaders.chnl_prod_trx", { product: selectedClient, channel }),
        align: "right",
        dataKey: "chnl_prod_trx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.comp_prod_trx`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.comp_prod_trx`, {
              product: selectedClient,
              channel,
            })
          : t("planProfile.topHcpColumnHeaders.comp_prod_trx", { product: selectedClient, channel }),
        align: "right",
        dataKey: "comp_prod_trx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.chnl_comp_trx`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.chnl_comp_trx`, {
              product: selectedClient,
              channel,
            })
          : t("planProfile.topHcpColumnHeaders.chnl_comp_trx", { product: selectedClient, channel }),
        align: "right",
        dataKey: "chnl_comp_trx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.chnl_mkt_trx`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.chnl_mkt_trx`, { channel })
          : t("planProfile.topHcpColumnHeaders.chnl_mkt_trx", { channel }),
        align: "right",
        dataKey: "chnl_mkt_trx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.prod_nrx`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.prod_nrx`, { product: selectedClient })
          : t("planProfile.topHcpColumnHeaders.prod_nrx", { product: selectedClient }),
        align: "right",
        dataKey: "prod_nrx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.chnl_prod_share`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.chnl_prod_share`, {
              product: selectedClient,
              channel,
            })
          : t("planProfile.topHcpColumnHeaders.chnl_prod_share", { product: selectedClient, channel }),
        align: "right",
        dataKey: "chnl_prod_share",
        format: {
          suffix: "%",
          decimalScale: 1,
          fixedDecimalScale: true,
        },
      },
    ],
    [channel, selectedClient, t, i18n, client_indication_key, timePeriod],
  );
  return availableColumns;
};

const HealthcareProviders: React.FC = () => {
  const [t] = useTranslation();
  const { client: selectedClient, id, hcpID } = useParams() as { client: string; id: string; hcpID: string };
  const { data: metaData } = useMetaData(selectedClient);
  const { sharedFilters, detailFilters, detailSort } = usePayerLandscapeState();
  const { updateSharedFilters, updateDetailFilters, updateDetailSort } = usePayerLandscapeDispatch();
  const indicationOptions = useClientIndicationFiltersconfiguration(selectedClient);
  const { indicationFiltering: showIndicationFiltering } = useClientPlanProfileConfiguration(selectedClient);
  // Indication sent to the API: undefined unless it is valid for the client in the URL.
  const indication = showIndicationFiltering
    ? reconcileIndicationFilter(detailFilters.indication, indicationOptions)
    : undefined;
  const configurationLoaded = useClientConfigurationLoaded(selectedClient);
  // Requests wait for the client's configuration and, when it filters by indication, a valid indication.
  const indicationReady = configurationLoaded && (!showIndicationFiltering || indication !== undefined);
  const { data: dataWindows } = useDataWindows(selectedClient);
  const timePeriodOptions = (dataWindows || []).map((dataWindow) => {
    return {
      label: t(`payerLandscape.timePeriodOptions.${dataWindow}`),
      value: dataWindow,
    };
  });
  const defaultDataWindow =
    (dataWindows || []).find((dataWindow) => dataWindow === DataWindow.twelveMonths) || DataWindow.twelveMonths;
  const showTimePeriodSelection = (dataWindows || []).length > 1;
  const healthcareQuery = useHealthcareProviders(
    selectedClient,
    id,
    sharedFilters.timePeriod || defaultDataWindow,
    hcpID as string,
    indication,
    detailSort,
    indicationReady,
  );

  const firstPageTableData = healthcareQuery?.data?.pages[0];
  const channel = firstPageTableData !== undefined && firstPageTableData !== null ? firstPageTableData[0].channel : "";
  const { data: role } = useGetRole(selectedClient);
  const {
    dataKeys: allowedDataKeys,
    sortTLCDataKey,
    sortTLCDirection,
    useIndicationInNames,
  } = useIndicationBasedTopHCPConfiguration(selectedClient, indication, String(role));

  const availableColumns = useTableColumns(channel as string, useIndicationInNames, indication);

  const columns: Column[] = useMemo(() => {
    if (!allowedDataKeys) return [];

    const columnMap = new Map<string, Column>();
    availableColumns.forEach((column: Column) => {
      columnMap.set(column.dataKey, column);
    });

    return allowedDataKeys
      .map((dataKey: string) => columnMap.get(dataKey))
      .filter((column: Column | undefined): column is Column => {
        return column !== undefined;
      });
  }, [availableColumns, allowedDataKeys]);

  const columnNames = columns.map((column) => column.label);
  const columnKeys = columns.map((column) => column.dataKey);

  //IF INDICATION SET, BUT SHOULDNT BE, SET TO UNDEFINED
  useEffect(() => {
    if (!showIndicationFiltering) {
      updateDetailFilters({ indication: undefined });
    }
  }, [indicationOptions, detailFilters.indication, showIndicationFiltering, updateDetailFilters]);

  //IF NO VALID INDICATION SET, SET TO FIRST INDICATION AVAILABLE
  useEffect(() => {
    if (showIndicationFiltering && indicationOptions && indicationOptions.length > 0 && indication === undefined) {
      updateDetailFilters({ indication: indicationOptions[0].value });
    }
  }, [indicationOptions, indication, showIndicationFiltering, updateDetailFilters]);

  //Set the default timePeriod to last 12 months
  useEffect(() => {
    if (!sharedFilters.timePeriod && (dataWindows || []).length > 0) {
      updateSharedFilters({ timePeriod: defaultDataWindow });
    }
  }, [dataWindows, defaultDataWindow, sharedFilters.timePeriod, updateSharedFilters]);

  const exportTable = usePayerProfileTopHCPExport(
    id,
    columnKeys,
    columnNames,
    selectedClient,
    100,
    sharedFilters.timePeriod ? sharedFilters.timePeriod : undefined,
    indication,
  );

  // Apply default sort on component mount
  useEffect(() => {
    if (sortTLCDataKey) {
      updateDetailSort({
        field: sortTLCDataKey,
        direction: (sortTLCDirection as SortDirection) || SortDirection.Ascending,
      });
    }
  }, [selectedClient, sortTLCDataKey, sortTLCDirection, updateDetailSort]);

  return (
    <>
      <PageHeader
        title={t([`planProfile.healthcareProviders.${selectedClient}`, "planProfile.healthcareProviders.default"])}
      ></PageHeader>

      <BreadCrumbs />

      <div className="pb-4">
        <div className="flex flex-col lg:flex-row-reverse">
          <div className="lg:w-1/3">
            {showTimePeriodSelection ? (
              <Select
                value={sharedFilters.timePeriod}
                label={t("payerLandscape.timePeriod")}
                options={timePeriodOptions}
                handleChange={(value) => updateSharedFilters({ timePeriod: value })}
              />
            ) : null}
          </div>
          <div className="lg:w-2/12">
            {indicationOptions && showIndicationFiltering && (
              <MinimalSelect
                value={detailFilters.indication}
                handleChange={(value) => updateDetailFilters({ indication: value })}
                label={t("payerLandscape.indicationFor.default")}
                options={indicationOptions}
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-row-reverse mb-2">
        <Tooltip placement="bottom-end" tip={t(`planProfile.topProviderTooltip.${selectedClient}`) as string}>
          <InfoOutlined className="text-primary" />
        </Tooltip>
      </div>

      <Table
        columns={columns}
        query={healthcareQuery}
        stickyMargin={54}
        metaData={metaData}
        export={exportTable}
        sort={detailSort}
        handleSort={(newSort) => updateDetailSort(newSort)}
      />
    </>
  );
};

export default HealthcareProviders;
